import { Alert, Col, Row, Space, Statistic, Table, Tag, Typography } from 'antd';
import type {
  SecurityEstimateLineResponse,
  SecurityEstimateResponse,
  SecurityProductEstimateResponse,
} from '@wisecure/api-client';
import { neutral } from '@/theme';
import { FORMULAS, FORMULA_LABELS, STUDY_REASON_LABELS, formatEuro, formatHalfDays } from './labels';

const { Text } = Typography;

const PRODUCTS = [
  { key: 'alarm', label: 'Alarme' },
  { key: 'video', label: 'Vidéo' },
  { key: 'options', label: 'Options' },
] as const;

const LINE_COLUMNS = [
  {
    title: 'Réf.',
    dataIndex: 'sku',
    key: 'sku',
    width: 120,
    render: (sku: string) => <Text style={{ fontFamily: 'monospace', fontSize: 12 }}>{sku}</Text>,
  },
  { title: 'Équipement', dataIndex: 'label', key: 'label' },
  {
    title: 'Variante',
    dataIndex: 'variant',
    key: 'variant',
    render: (variant?: string | null) => variant || '—',
  },
  { title: 'Qté', dataIndex: 'quantity', key: 'quantity', width: 70, align: 'right' as const },
];

/** Plan prices of a product, e.g. "Essentiel 2 170 € · Confort 2 910 € · Premium 3 410 €". */
export function FormulaPrices({ product }: { product: SecurityProductEstimateResponse }) {
  if (!product.formulaPrices) return null;
  const prices = product.formulaPrices;
  return (
    <Space size={[4, 4]} wrap>
      {FORMULAS.map((formula) => (
        <Tag key={formula} color={formula === product.formula ? 'blue' : undefined}>
          {FORMULA_LABELS[formula]} {formatEuro(prices[formula])}
        </Tag>
      ))}
    </Space>
  );
}

function ProductSection({ label, product }: { label: string; product: SecurityProductEstimateResponse }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 8,
          marginBottom: 8,
        }}
      >
        <Space size={8}>
          <Text strong>{label}</Text>
          {product.formula && <Tag color="blue">{FORMULA_LABELS[product.formula]}</Tag>}
          <Text style={{ color: neutral[500] }}>{product.itemCount} élément(s)</Text>
        </Space>
        <Space size={12} wrap>
          <FormulaPrices product={product} />
          <Text strong>{formatEuro(product.price)}</Text>
        </Space>
      </div>
      <Table<SecurityEstimateLineResponse>
        columns={LINE_COLUMNS}
        dataSource={product.lines}
        rowKey="sku"
        pagination={false}
        size="small"
      />
    </div>
  );
}

/** Totals and equipment lines of an estimate (client-facing figures). */
export function EstimateEquipment({ estimate }: { estimate: SecurityEstimateResponse }) {
  return (
    <div>
      {estimate.studyRequired && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Étude sur site nécessaire"
          description={estimate.studyReasons.map((r) => STUDY_REASON_LABELS[r] ?? r).join(', ')}
        />
      )}
      <Row gutter={[24, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Statistic title="Prix installé" value={estimate.studyRequired ? 'Sur étude' : formatEuro(estimate.totalPrice)} />
        </Col>
        <Col xs={12} md={6}>
          <Statistic title="Durée de pose" value={estimate.installHalfDays ? formatHalfDays(estimate.installHalfDays) : '—'} />
        </Col>
        <Col xs={12} md={6}>
          <Statistic title="Détecteurs" value={estimate.detectorCount} />
        </Col>
        <Col xs={12} md={6}>
          <Statistic title="Caméras" value={estimate.cameraCount} />
        </Col>
      </Row>
      {PRODUCTS.map(({ key, label }) => {
        const product = estimate[key];
        return product?.lines?.length ? <ProductSection key={key} label={label} product={product} /> : null;
      })}
    </div>
  );
}
